import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

interface OrganizationSettings {
  id: string;
  organization_name: string;
  logo_url: string | null;
  primary_color: string;
  accent_color: string;
}

export const useOrganizationSettings = () => {
  const [settings, setSettings] = useState<OrganizationSettings | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const { data, error } = await supabase
        .from("organization_settings")
        .select("*")
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        console.error("Error loading organization settings:", error);
      } else if (data) {
        setSettings(data);
        applyBranding(data);
      }
    } catch (error) {
      console.error("Error loading organization settings:", error);
    } finally {
      setLoading(false);
    }
  };

  const applyBranding = (settings: OrganizationSettings) => {
    // Apply colors to CSS variables
    const root = document.documentElement;
    
    // Convert hex to HSL for CSS variables
    const hexToHsl = (hex: string) => {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      if (!result) return null;

      const r = parseInt(result[1], 16) / 255;
      const g = parseInt(result[2], 16) / 255;
      const b = parseInt(result[3], 16) / 255;

      const max = Math.max(r, g, b);
      const min = Math.min(r, g, b);
      let h = 0, s = 0, l = (max + min) / 2;

      if (max !== min) {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        switch (max) {
          case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
          case g: h = ((b - r) / d + 2) / 6; break;
          case b: h = ((r - g) / d + 4) / 6; break;
        }
      }

      return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
    };

    const primaryHsl = hexToHsl(settings.primary_color);
    const accentHsl = hexToHsl(settings.accent_color);

    if (primaryHsl) {
      root.style.setProperty('--primary', primaryHsl);
    }
    if (accentHsl) {
      root.style.setProperty('--accent', accentHsl);
    }

    // Update page title
    document.title = `${settings.organization_name} - Steel Fabrication Assistant`;
  };

  return { settings, loading, refresh: loadSettings };
};